import React from 'react';

import Slider from 'common/ui/Slider';
import { H4, P, Span } from 'common/ui/Text';
import abbreviateNumber from 'common/util/abbreviateNumber';
import numberWithCommas from 'common/util/numberWithCommas';
import styles from 'css-module/components/www/_PricingV4.module.scss';

import PricingV4Plans from './PricingV4Plans';

import type { Timeframe } from './Timeframe';

const MaxSliderValue = '5k+';
const SliderValues = [
  '25',
  ...PricingV4Plans.core.pricePoints!.map((pricePoint) =>
    pricePoint.trackedUsers >= 3000
      ? abbreviateNumber(pricePoint.trackedUsers, 0)
      : numberWithCommas(pricePoint.trackedUsers),
  ),
  MaxSliderValue,
];

type Props = {
  timeframe: Timeframe;
  trackedUsers: number;
  setTrackedUsers: (trackedUsers: number) => void;
};
export default function PricingV4Slider({ timeframe, trackedUsers, setTrackedUsers }: Props) {
  const onSliderChange = (value: string) => {
    if (value === MaxSliderValue) {
      setTrackedUsers(5001);
    } else {
      if (value.includes('k')) {
        value = value.replace('k', ',000');
      }
      setTrackedUsers(Number(value.replace(',', '')));
    }
  };
  let sliderValue;
  if (trackedUsers === 5001) {
    sliderValue = MaxSliderValue;
  } else if (trackedUsers >= 3000) {
    sliderValue = abbreviateNumber(trackedUsers, 0);
  } else {
    sliderValue = numberWithCommas(trackedUsers);
  }
  return (
    <div className={styles.pricingV4Slider}>
      <div className={styles.sliderRightContainer}>
        <H4 variant="headingSm">What is a tracked&nbsp;user?</H4>
        <P className={styles.sliderRightDefinition} variant="bodyMd">
          A tracked user is any user associated with&nbsp;feedback.
        </P>
        <a href="#how-many-tracked-users-do-i-need">
          <Span className={styles.sliderRightQuestion} variant="bodyMd">
            How many do I&nbsp;need?
          </Span>
        </a>
      </div>
      <div className={styles.sliderLeftContainer}>
        <div className={styles.sliderHeader}>
          <HeaderDetails />
        </div>
        <Slider value={sliderValue} values={SliderValues} onChange={onSliderChange} />
        <HeaderPricing timeframe={timeframe} trackedUsers={trackedUsers} />
      </div>
    </div>
  );
}

function HeaderDetails() {
  return (
    <div className={styles.sliderDetails}>
      <P variant="headingSm">How many users will you track feedback from?</P>
      <P variant="bodyMd">
        Start low, scale up as you get more users. Set an optional spend&nbsp;limit.
      </P>
    </div>
  );
}

type HeaderPricingProps = {
  timeframe: Timeframe;
  trackedUsers: number;
};
function HeaderPricing({ timeframe, trackedUsers }: HeaderPricingProps) {
  const corePlan = PricingV4Plans.core;
  const proPlan = PricingV4Plans.pro;
  const corePricePoint = corePlan.pricePoints!.find((pricePoint) => {
    return pricePoint.trackedUsers >= trackedUsers;
  });
  const proPricePoint = proPlan.pricePoints!.find((pricePoint) => {
    return pricePoint.trackedUsers >= trackedUsers;
  });
  return (
    <ul className={styles.sliderPricing}>
      {corePricePoint && (
        <li className={styles.sliderPricingItem}>
          <Span variant="headingSm">Core</Span>
          <Span variant="bodyMd">{`$${corePricePoint?.[timeframe]}/mo`}</Span>
        </li>
      )}
      {proPricePoint && (
        <li className={styles.sliderPricingItem}>
          <Span variant="headingSm">Pro</Span>
          <Span variant="bodyMd">{`$${proPricePoint?.[timeframe]}/mo`}</Span>
        </li>
      )}
      {!corePricePoint && !proPricePoint && (
        <li className={styles.sliderPricingItem}>
          <Span variant="headingSm">Business</Span>
          <Span variant="bodyMd">Contact us</Span>
        </li>
      )}
      <li className={styles.sliderPricingItem}>
        <Span variant="bodyMd">{timeframe === 'yearly' ? 'Billed yearly' : 'Billed monthly'}</Span>
      </li>
    </ul>
  );
}
